/*
 * r16p (2026-10-24), DESIGN D:/NecroWork/dm-chats-evidence/DESIGN.md 6.3, 6.5, 6.6, 7.1, 7.6: the pure side of the V3 Inbox. No React,
 * no network: the server's answers in, the page's view model out. Every function tolerates a missing or older field (the server
 * round is built in parallel; this file reads both camelCase and snake_case) and never invents a number the server did not send.
 *
 * Privacy (design 7.6): message text, reply text and the people's names are third parties' personal data. Nothing in this file
 * logs; chatLog() below is the only console helper of the round and it takes counts, statuses and opaque ids only - it refuses
 * (drops) any value that is not a number, a boolean, a known status word or an opaque id.
 */

export const CHATS_TAG = '[R16P-UI]';

// ------------------------------------------------------------------------------------------------ small readers

const has = (o, k) => !!o && Object.prototype.hasOwnProperty.call(o, k);
const pick = (o, ...keys) => {
  if (!o || typeof o !== 'object') return undefined;
  for (let i = 0; i < keys.length; i++) if (has(o, keys[i]) && o[keys[i]] !== undefined) return o[keys[i]];
  return undefined;
};
const num = (v) => {
  if (v === null || v === undefined || v === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};
const str = (v) => (typeof v === 'string' && v.trim() !== '' ? v : null);
/*
 * r16pm review MUST-FIX (2026-10-25): a paging cursor. The server's thread detail sends `olderCursor` as a JSON NUMBER (the seq of the
 * oldest message it kept: ChatsUiController.thread, Long.valueOf(seq)), the list's `nextCursor` as an opaque string. str() alone read
 * the number as null, so a thread of more than 50 messages never offered "Load older messages". Both now become a non-empty string
 * (a whole number is exact: seqs are far below 2^53; '0' is a real cursor), anything else (a fraction, NaN, an object) null.
 */
export const cursorOf = (v) => {
  if (typeof v === 'number') return Number.isFinite(v) && Number.isInteger(v) && v >= 0 ? String(v) : null;
  return str(v);
};
const bool = (v) => v === true || v === 1 || v === 'true' || v === '1';
const lower = (v) => (typeof v === 'string' ? v.trim().toLowerCase() : null);

/** epoch ms (number or numeric string) or an ISO / RFC 3339 string -> epoch ms, else null. */
export function toMs(v) {
  if (v === null || v === undefined || v === '') return null;
  if (typeof v === 'number') return Number.isFinite(v) ? v : null;
  const s = String(v).trim();
  if (/^\d{10,}$/.test(s)) return Number(s);
  const t = Date.parse(s);
  return Number.isFinite(t) ? t : null;
}

// ------------------------------------------------------------------------------------------------ opaque ids and the log

/** An opaque server id (conv_..., rpl_..., msg_..., job ids, key ids) - letters, digits, _ and -, at most 64 characters. */
export const isOpaqueId = (v) => typeof v === 'string' && /^[A-Za-z0-9_-]{1,64}$/.test(v);

const STATUS_WORDS = new Set(['queued', 'claimed', 'sending', 'held', 'sent', 'unconfirmed', 'failed', 'cancelled',
  'pending', 'done', 'on', 'off', 'full', 'preview', 'ok', 'error', 'routeMissing', 'notEnabled', 'refused', 'other',
  'rateLimited', 'sessionRequired', 'noAccess', 'unread', 'needs_reply', 'all', 'detached', 'instagram', 'tiktok', 'FULL', 'PREVIEW',
  'OFF', 'new_inbound', 'confirm_peer', 'replay', 'ambiguous', 'definitive', 'unknown']);

/*
 * Review (2026-10-24): the log guard used to pass ANY [A-Za-z0-9_-]{1,64} string as an "opaque id" and any lower snake case word as
 * a "code" - so 'Riya', 'studiolumen', 'hello' or 'call_me_at_9876543210' would have been printed had a call site ever passed one.
 * Now a value prints only when it is
 *   - one of the server's public ids: conv_ / rpl_ / msg_ / sug_ (ChatIds: prefix + 20 base32) and the job_ / key_ ids V3 also
 *     sees - a known prefix, then letters and digits only (no '.', no '-': never a handle);
 *   - a slot NAME of the slot<number> shape;
 *   - a word this bundle knows: a status above, or a code of CHAT_ERROR_WORDS / FAIL_CODE_WORDS / BLOCKED_REASON_WORDS (added
 *     below, once those tables exist). An unknown server code prints as '?' - its HTTP status is still on the line.
 */
const LOGGABLE_ID = /^(conv|rpl|msg|sug|job|key)_[A-Za-z0-9]{1,40}$/;
const SLOT_NAME_RE = /^slot\d{1,7}$/;
const KNOWN_WORDS = new Set(STATUS_WORDS);

/** A value chatLog may print as an id: a known-prefix public id or a slot<number> name. */
export const isLoggableId = (v) => typeof v === 'string' && (LOGGABLE_ID.test(v) || SLOT_NAME_RE.test(v));

/**
 * The one console line of the round: `[R16P-UI] <event> k=v ...`. Values must be numbers, booleans, null, words this bundle knows
 * (statuses, codes) or known-prefix ids (above); anything else (a text, a name, a handle, an unknown word) is replaced by `?` and
 * never printed. Returns the line (tests read it) and prints it with console.info / console.warn.
 */
export function chatLog(level, event, fields = {}) {
  return chatLogTagged(CHATS_TAG, level, event, fields);
}

/**
 * r16q (2026-10-27): the same guarded line under another tag of this bundle (`[R16Q-UI]`: the chat request guard's decisions), so
 * each round's decisions can be found by their own tag. Only a fixed tag of the code is accepted; anything else is `[R16P-UI]`.
 */
const LOG_TAGS = new Set([CHATS_TAG, '[R16Q-UI]']);
export function chatLogTagged(tag, level, event, fields = {}) {
  // the event is a constant of the code (never data): words, spaces, '/', ':' and '-' are kept
  const parts = [LOG_TAGS.has(tag) ? tag : CHATS_TAG, String(event).replace(/[^A-Za-z0-9_.:/ -]/g, '').slice(0, 80)];
  Object.keys(fields || {}).forEach((k) => {
    const v = fields[k];
    let out = '?';
    if (v === null || v === undefined) out = '-';
    else if (typeof v === 'number' && Number.isFinite(v)) out = String(v);
    else if (typeof v === 'boolean') out = v ? 'true' : 'false';
    else if (typeof v === 'string' && (KNOWN_WORDS.has(v) || isLoggableId(v))) out = v;
    parts.push(`${String(k).replace(/[^A-Za-z0-9_]/g, '')}=${out}`);
  });
  const line = parts.join(' ');
  try {
    // eslint-disable-next-line no-console
    (level === 'warn' ? console.warn : console.info)(line);
  } catch (e) { /* a console that throws must not break the page */ }
  return line;
}

/** A fresh client request id for one press of Send (idempotent per id on the server, design 6.6 / R9). */
export function newClientRequestId() {
  try {
    if (typeof crypto !== 'undefined' && crypto && typeof crypto.randomUUID === 'function') return crypto.randomUUID();
  } catch (e) { /* fall through */ }
  const r = () => Math.floor(Math.random() * 0x100000000).toString(16).padStart(8, '0');
  return `${r()}-${r().slice(0, 4)}-4${r().slice(0, 3)}-a${r().slice(0, 3)}-${r()}${r().slice(0, 4)}`;
}

// ------------------------------------------------------------------------------------------------ errors

/**
 * Codes the r16p chat routes answer as { error, message } (design 6.5, 6.6, 6.7 deltas). The message shown is always the server's
 * own `message` when there is one (it never quotes text, design 6.6), else the words below.
 */
export const CHAT_ERROR_WORDS = {
  not_enabled: 'Chats are switched off on this server.',
  not_configured: 'Chats are not set up on this server yet.',
  session_required: 'Sign in again to use the inbox.',
  https_required: 'The inbox needs a secure (https) connection.',
  slot_not_found: 'That slot is not on this account.',
  thread_not_found: 'That conversation is not on this account any more.',
  platform_not_supported: 'The inbox is Instagram-only for now.',
  slot_lookup_unavailable: 'The server could not check the slot right now. Try again in a minute.',
  reply_already_claimed: 'The worker has already picked this reply up, so it can no longer be cancelled.',
  reply_not_held: 'This reply is not waiting for you any more.',
  not_held: 'This reply is not waiting for you any more.',
  reply_final: 'This reply is already finished (sent, failed or cancelled).',
  not_requeueable: 'Only a reply that was not confirmed or that failed can be queued again.',
  not_found: 'That is not on this account any more.',
  text_empty: 'Write something first.',
  text_too_long: 'The reply is too long.',
  text_control_chars: 'The reply holds characters that cannot be typed on the phone.',
  account_queue_full: 'This account already has the most queued replies it can hold.',
  reply_not_requeueable: 'Only a reply that was not confirmed or that failed can be queued again.',
  reply_too_long: 'The reply is too long.',
  text_rejected: 'The reply text was refused (empty, or it holds characters Instagram cannot take).',
  duplicate_recent: 'You sent the same text in this thread recently.',
  broadcast_refused: 'The same text is already queued to many conversations today, so it was refused.',
  link_cap: 'Too many replies with a link today on this slot.',
  typing_too_long: 'This reply would take too long to type on the phone.',
  account_send_cap: 'The account has reached its reply limit for now.',
  slot_queue_full: 'This slot already has the most queued replies it can hold.',
  conversation_queue_full: 'This conversation already has the most queued replies it can hold.',
  replies_not_allowed: 'Replies are not possible in this conversation.',
  replies_disabled: 'Replies are switched off for this account.',
  idempotency_key_reused: 'This send was already used for a different text. Press Send again.',
  conversation_busy: 'A reply in this conversation is being sent right now. Try again after it finishes.',
  rate_limited: 'Too many requests. Wait a moment and try again.',
  ai_busy: 'AI suggestions are busy. Try again in a minute.',
  ai_budget_exhausted: "Today's AI suggestions are used up.",
  ai_not_configured: 'AI suggestions are not set up yet.',
  ai_disabled: 'AI suggestions are switched off.',
  ai_declined: 'The AI gave no suggestion for this conversation.',
  ai_bad_output: 'The AI gave no usable suggestion. Try again.',
  ai_timeout: 'No suggestion arrived in time. Try again.',
  key_limit: 'This account already has the most API keys it can hold.',
  api_disabled: 'The API is switched off on this server.',
  // r16pm merge (2026-10-24): the r16p server's own codes (NSB-r16p chats/ui/ChatUiAccess.MESSAGES). The server always sends its
  // own `message`, which wins; these words are the fallback and make the codes printable in chatLog lines. NOT here on purpose:
  // `busy` and `internal_error` (a reply POST answered with either may still be stored - see isAmbiguousSendError) and the 401
  // codes (SESSION_CODES below).
  chats_disabled: 'Chats are switched off on this server.',
  invalid_request: 'The request is not valid.',
  invalid_cursor: 'The list position is no longer valid. Reload the list.',
  invalid_filter: 'Unknown filter.',
  payload_too_large: 'The request is too large.',
  reply_state_conflict: 'This reply can no longer be changed.',
  already_sent: 'This text is already confirmed as sent in this conversation; it is not queued again.',
  already_requeued: 'A copy of this reply is already waiting to be sent.',
  text_erased: 'The text of this reply was already removed.',
  retention_invalid: 'Retention must be one of the offered choices.',
  slot_chat_sync_off: 'The slot does not read its DM inbox (setting 771).',
  slot_reply_mode_off: 'Replies are off for this slot (setting 772).',
  conversation_detached: 'The slot now runs a different Instagram account; this conversation is read-only.',
  request_not_accepted: 'Message requests cannot be replied to.',
  group_reply_not_supported: 'Group conversations cannot be replied to.',
  peer_ambiguous: 'Two conversations show the same name; this one cannot be replied to.',
  no_recent_inbound_message: 'There is no recent message from the other person to reply to.',
  outbound_streak: 'Three of your messages in a row already wait for an answer.',
  thread_daily_cap: 'This conversation reached today\'s reply limit.',
  account_replies_disabled: 'Replies are switched off for this account.',
  key_limit_reached: 'This account already has the most active API keys it can hold.',
  name_required: 'Give the key a name.',
  scope_required: 'Pick at least one permission.',
  unknown_scope: 'Unknown permission.',
  slot_required: 'Pick at least one slot, or all slots.',
  too_many_slots: 'A key can cover at most 5,000 slots.',
  expiry_invalid: 'Pick one of the offered expiry choices.',
  account_inactive: 'This account is not active.',
  ai_slot_mode: 'AI suggestions are off for this slot (setting 772).',
  ai_no_messages: 'There is no stored message in this conversation to suggest a reply to.',
  // the MERGED server (D:/NecroWork/merge-r16pm/SERVER-CONTRACT.md 2d): a login with no chat access - an Inventory client login - gets
  // 403 no_chat_access on the chat routes (not on the three API-key levers). Classified noAccess below (NO_ACCESS_CODES), never
  // 'refused', although it is a known word.
  no_chat_access: 'This login has no access to the chat pages.',
};

/** The 401 codes of the chat routes (ChatUiAccess.open): the login has no chat session - "no inbox here", never a redirect on reads. */
const SESSION_CODES = new Set(['session_required', 'session_expired', 'unauthenticated']);
/** 5xx codes after which a reply POST may have been stored (ChatsUiController.created reads the view after the create committed). */
const MAYBE_STORED_5XX = new Set(['busy', 'internal_error']);
/** Codes of the switched-off server (`not_enabled` was V3's guess; `chats_disabled` is the r16p server's real 404 code). */
const OFF_CODES = new Set(['not_enabled', 'chats_disabled']);
/** The merged server's 403 for a login with no chat access (SERVER-CONTRACT.md 2d): no Inbox, no retry, no /login bounce. */
const NO_ACCESS_CODES = new Set(['no_chat_access']);
/** Refusal codes after which the conversation is read again: its eligibility changed (the closed composer then says why). */
export const RELOAD_ON_CODES = new Set(['replies_not_allowed', 'thread_not_found', 'not_found', 'conversation_detached', 'request_not_accepted',
  'group_reply_not_supported', 'peer_ambiguous', 'no_recent_inbound_message', 'outbound_streak', 'thread_daily_cap', 'slot_chat_sync_off',
  'slot_reply_mode_off', 'account_replies_disabled', 'platform_not_supported',
  // r16q (the r16pdc README section 4 item 5): the merged server's 422 for a conversation last read in Instagram's All tab or an
  // unproven tab (ChatFolders.NOT_RULED_OUT) - its eligibility changed since the page read it (switch necro.ui.r16q.reloadNotRuledOut)
  'request_not_ruled_out']);
/** The codes r16q added to RELOAD_ON_CODES (each behind its necro.ui.r16q part). */
const R16Q_RELOAD_CODES = new Set(['request_not_ruled_out']);

/**
 * r16q: whether a send refused with this code re-reads the conversation. A code of R16Q_RELOAD_CODES only while its switch is on
 * (off = r16sd: such a refusal only toasts the server's sentence; the closed composer shows at the next poll).
 */
export function reloadsOnCode(code, notRuledOutOn = true) {
  if (!code || !RELOAD_ON_CODES.has(code)) return false;
  return !R16Q_RELOAD_CODES.has(code) || !!notRuledOutOn;
}

const CODE_RE = /^[a-z][a-z0-9_]{1,48}$/;

/**
 * @returns {{ kind: 'routeMissing'|'notEnabled'|'sessionRequired'|'rateLimited'|'refused'|'other', status, code, message, details,
 *             retryAfterSec }}
 * routeMissing = a 404 / 405 with no code of ours: a server before r16p (draw nothing, one console line). notEnabled = the server's
 * chat switch is off (`not_enabled`, or a body `{r: 'OFF'}`). sessionRequired = `session_required` or any 401 (the chat reads skip
 * the global /login redirect, lib/chatsApi.js). noAccess = a 403 with no code of ours (a login type without chat access).
 * refused = any other code of ours, with the server's words.
 */
export function classifyChatsError(err) {
  const res = err && err.response;
  const status = res && typeof res.status === 'number' ? res.status : null;
  const data = res && res.data && typeof res.data === 'object' ? res.data : null;
  const rawCode = data && typeof data.error === 'string' ? data.error.trim() : null;
  const code = rawCode && CODE_RE.test(rawCode) ? rawCode : null;
  const message = data ? (str(data.message) || (typeof data.detail === 'string' && data.detail !== rawCode ? str(data.detail) : null)) : null;
  const details = data && data.details && typeof data.details === 'object' ? data.details : (data || null);
  const retryAfterSec = num(pick(data, 'retryAfterSeconds', 'retryAfterSec', 'retry_after_sec', 'retryAfter'));
  // typing_too_long: the server's estimate in minutes (ChatReplyStore: estMinutes); the mock's details.minutes is still read
  const est = num(pick(data, 'estMinutes', 'est_minutes'));
  const estMinutes = est !== null ? est : num(data && data.details && typeof data.details === 'object' ? data.details.minutes : null);
  let kind = 'other';
  if ((code && OFF_CODES.has(code)) || (data && lower(data.r) === 'off')) kind = 'notEnabled';
  else if ((code && SESSION_CODES.has(code)) || (status === 401 && !(code && has(CHAT_ERROR_WORDS, code)))) kind = 'sessionRequired';
  else if ((code && NO_ACCESS_CODES.has(code)) || (status === 403 && !(code && has(CHAT_ERROR_WORDS, code)))) kind = 'noAccess';
  else if (code === 'rate_limited' || (status === 429 && !code)) kind = 'rateLimited';
  else if (code && has(CHAT_ERROR_WORDS, code)) kind = 'refused';
  else if (status === 404 || status === 405) kind = 'routeMissing';
  else if (code && status && status >= 400 && status < 500) kind = 'refused';
  return { kind, status, code, message, details, retryAfterSec, estMinutes };
}

/** The sentence for a failed chat call: the server's message, else the words for its code, else the fallback + status. */
export function chatsErrorText(err, fallback = 'The request failed') {
  const c = classifyChatsError(err);
  if (c.message) return c.message;
  if (c.code && CHAT_ERROR_WORDS[c.code]) return CHAT_ERROR_WORDS[c.code];
  if (c.status) return `${fallback} (HTTP ${c.status})`;
  return err && err.code === 'ECONNABORTED' ? `${fallback} (no answer in time)` : `${fallback} (no answer)`;
}

/*
 * Review P2 (2026-10-24): a send whose answer never came (a timeout, no network, a 5xx without a code of ours) is AMBIGUOUS - the
 * reply may be stored. The page then keeps that exact request (same clientRequestId, same body) for the same conversation and the
 * same text, so the next press of Send is a replay the server answers from the stored row (design 6.6: idempotent per
 * clientRequestId with a body-hash compare) instead of a second reply to a real person. A definitive answer (any 2xx, any 4xx, a
 * 5xx with a code of ours) ends that.
 */
export function isAmbiguousSendError(err) {
  const res = err && err.response;
  if (!res || typeof res.status !== 'number') return true;
  if (res.status === 408) return true;
  if (res.status >= 500) {
    const data = res.data && typeof res.data === 'object' ? res.data : null;
    const code = data && typeof data.error === 'string' ? data.error.trim() : null;
    // r16pm: `busy` / `internal_error` can come AFTER the reply row committed (the view read failed), so they stay ambiguous
    if (code && MAYBE_STORED_5XX.has(code)) return true;
    return !(code && has(CHAT_ERROR_WORDS, code));
  }
  return false;
}

export const AMBIGUOUS_SEND_TEXT = 'No answer came back, so this reply may have been queued. Check the conversation (it is being '
  + 'reloaded) before sending again: Send with the same text repeats the same request, so it cannot be queued twice.';

/** The key of one pending attempt: the conversation and the text as the server will normalise it. */
export function sendAttemptKey(threadId, text) {
  return `${threadId || '-'}|${normForLength(text)}`;
}

// ------------------------------------------------------------------------------------------------ info and summary

export const DEFAULT_MAX_REPLY = 1000;

/** GET /uiapi/chats/info -> the page's switches. Missing = the safe answer (AI off, no newlines, 1,000 characters). */
export function normaliseInfo(raw) {
  const r = raw && typeof raw === 'object' ? raw : {};
  const ai = r.ai && typeof r.ai === 'object' ? r.ai : {};
  const platforms = r.platforms && typeof r.platforms === 'object' ? r.platforms : {};
  const max = num(pick(r, 'maxReplyLength', 'max_reply_length'));
  const api = r.api && typeof r.api === 'object' ? r.api : null;
  return {
    enabled: r.enabled !== false && lower(r.r) !== 'off',
    platforms: { INSTAGRAM: platforms.INSTAGRAM !== false, TIKTOK: platforms.TIKTOK === true },
    tiktokNote: str(pick(r, 'tiktokNote', 'tiktok_note')),
    ai: {
      enabled: ai.enabled === true,
      configured: ai.configured === true,
      usedToday: num(pick(ai, 'usedToday', 'used_today')),
      limit: num(pick(ai, 'limit', 'dailyLimit', 'daily_limit')),
      processorNotice: str(pick(ai, 'processorNotice', 'processor_notice')),
    },
    retentionDays: num(pick(r, 'retentionDays', 'retention_days')),
    maxReplyLength: max && max > 0 ? Math.min(max, 4000) : DEFAULT_MAX_REPLY,
    newlines: r.newlines === true,
    cadenceNote: str(pick(r, 'cadenceNote', 'cadence_note')),
    api,
    // r16pm: the r16p server's info (ChatsUiController.info) - `configured` false = the chat routes that need the data key answer
    // 503 not_configured; the reply window and the retention choices are its numbers (none is invented here)
    configured: r.configured !== false,
    replyWindowDays: num(pick(r, 'replyWindowDays', 'reply_window_days')),
    retentionChoices: retentionChoicesOf(pick(r, 'retentionChoices', 'retention_choices')),
    repliesDisabled: r.repliesDisabled === true,
    sendCaps: sendCapsOf(r.sendCaps),
  };
}

/** The server's retention choices in days (positive integers, ascending, de-duplicated); [] when not sent. */
export function retentionChoicesOf(v) {
  if (!Array.isArray(v)) return [];
  return Array.from(new Set(v.map((x) => num(x)).filter((x) => x !== null && x > 0 && Number.isInteger(x)))).sort((a, b) => a - b);
}

function sendCapsOf(v) {
  if (!v || typeof v !== 'object') return null;
  return { daily: num(v.daily), hourly: num(v.hourly), perSlotQueued: num(v.perSlotQueued), perConversationQueued: num(v.perConversationQueued) };
}

/** GET /uiapi/chats/summary -> counts (null = not sent by this server; drawn as a dash, never 0). */
export function normaliseSummary(raw) {
  const r = raw && typeof raw === 'object' ? raw : {};
  return {
    unreadThreads: num(pick(r, 'unreadThreads', 'unread_threads')),
    needsReply: num(pick(r, 'needsReply', 'needs_reply', 'needsReplyThreads')),
    slotsWithUnread: num(pick(r, 'slotsWithUnread', 'slots_with_unread')),
    queuedReplies: num(pick(r, 'queuedReplies', 'queued_replies')),
    heldReplies: num(pick(r, 'heldReplies', 'held_replies')),
    unconfirmedReplies: num(pick(r, 'unconfirmedReplies', 'unconfirmed_replies')),
    failedReplies: num(pick(r, 'failedReplies', 'failed_replies')),
    requestsUnreadSlots: num(pick(r, 'slotsWithRequests', 'requestsUnreadSlots', 'requests_unread_slots', 'requestsUnread')),
    asOf: toMs(pick(r, 'asOf', 'as_of')),
  };
}

/** The nav pill: threads with unread, capped "99+"; null (no pill) for 0 or not sent. */
export function navPillText(summary) {
  const n = summary && summary.unreadThreads;
  if (!Number.isFinite(n) || n <= 0) return null;
  return n > 99 ? '99+' : String(n);
}

// ------------------------------------------------------------------------------------------------ reply states

/** DB upper case / API lower case -> one lower-case word (design section 2). */
export function replyStatus(v) {
  const s = lower(v);
  if (!s) return null;
  if (s === 'canceled') return 'cancelled';
  if (s === 'not_confirmed' || s === 'unknown') return 'unconfirmed';
  return ['queued', 'claimed', 'sending', 'held', 'sent', 'unconfirmed', 'failed', 'cancelled'].includes(s) ? s : null;
}

/** The chip for each state: label, tone (a token name), and whether the worker may still act on it. */
export const REPLY_STATUS = {
  queued: { label: 'Queued', tone: 'info', open: true, hint: 'Waiting for the slot\'s next session on the phone.' },
  claimed: { label: 'Picked up', tone: 'info', open: true, hint: 'The worker has picked this reply up for this session.' },
  sending: { label: 'Sending', tone: 'primary', open: true, hint: 'The worker is typing or sending it now.' },
  held: { label: 'Waiting for you', tone: 'warning', open: true, hint: 'Nothing is sent until you choose below.' },
  sent: { label: 'Sent', tone: 'success', open: false, hint: 'The worker saw it go out in the thread.' },
  unconfirmed: { label: 'Not confirmed', tone: 'warning', open: false, hint: 'We could not confirm it was sent. It will not be sent again on its own.' },
  failed: { label: 'Not sent', tone: 'danger', open: false, hint: 'It was not sent.' },
  cancelled: { label: 'Cancelled', tone: 'muted', open: false, hint: 'Cancelled before it was sent.' },
  // review: a state this bundle does not know (a newer server) - shown as it is, no action offered, not counted as open, no fast poll
  other: { label: 'Unknown state', tone: 'muted', open: false, hint: 'This page does not know this reply\'s state. Reload later or check on the phone.' },
};

export const OPEN_STATES = new Set(['queued', 'claimed', 'sending', 'held']);

/*
 * Outcome codes (design 5.8) -> what the customer reads under a FAILED reply. FAILED is FINAL (design 6.3): a retryable code that
 * reaches FAILED means its tries ran out (3), so no line here says "it is tried again" (review: the old words promised a retry
 * that never comes). Unknown codes show the server's own message instead.
 */
export const FAIL_CODE_WORDS = {
  thread_not_found: 'The worker did not find this conversation in the inbox pages it reads (it may be older than them).',
  time_cap: 'Each session ran out of time before it was typed, so it was not sent.',
  server_ack_failed: 'The phone could not reach the server before sending, so nothing was sent.',
  composer_unavailable: 'The message box could not be used on the phone, so it was not sent.',
  not_sent_proven: 'The phone showed it was not sent.',
  dm_paused: 'Instagram limited DMs on this account, so it was not sent.',
  peer_unverified: 'The worker could not prove who the conversation is with, so it was not sent.',
  draft_present: 'A draft is in the message box on the phone, so the worker typed nothing (it never clears your draft).',
  row_moved: 'The inbox kept changing while the worker was reading it, so it was not sent.',
  wrong_thread: 'The worker opened a different conversation, so it sent nothing.',
  peer_ambiguous: 'Two conversations have the same name, so the worker cannot tell them apart.',
  group_unknown: 'The worker could not prove this is a one-to-one conversation.',
  group_thread: 'Replies to group conversations are not supported.',
  no_inbound: 'There is no message from the other person to reply to.',
  account_mismatch: 'The phone showed a different Instagram account than this slot\'s.',
  account_changed: 'The slot\'s Instagram account changed.',
  owner_changed: 'The slot moved to another account.',
  request_not_accepted: 'This is a message request that was not accepted on the phone.',
  thread_restricted: 'Instagram does not allow replies in this conversation.',
  meta_ai_screen: 'The phone showed a Meta AI screen instead of the conversation.',
  text_rejected: 'Instagram refused the text.',
  expired: 'It waited longer than its time limit, so it was not sent.',
  conversation_detached: 'The conversation is no longer in this slot\'s inbox.',
  slot_mode_off: 'Replies are switched off for this slot.',
  unknown_after_tap: 'Send was pressed on the phone but the result could not be confirmed.',
  confirmed_late: 'Confirmed later from the conversation.',
  lease_expired: 'The worker did not finish in time, so it was not sent.',
  replies_disabled: 'Replies were switched off for this account, so it was not sent.',
  // r16q (the r16pdc README section 4 item 3): the merged server's own sentence (ChatReplyCodes.INBOUND_OUT_OF_VIEW_MESSAGE, a copy
  // checked against __fixtures__/chatGuard.r16q.json), so a server that sends no `message` (inboundOutOfView=false, r16pd) still says
  // it; shown only while necro.ui.r16q.outOfView is on (off = r16sd: the server's `message`, else 'It was not sent.')
  inbound_out_of_view: 'The person\'s last message is too far up the chat for the worker to check, so it was not sent. Reply by hand on the phone.',
};
/** The fail codes r16q added to FAIL_CODE_WORDS (each behind necro.ui.r16q.outOfView). */
export const R16Q_FAIL_CODES = new Set(['inbound_out_of_view']);

/**
 * r16q (S4, the r16pdc README section 4 item 2): why a QUEUED reply waits for its conversation's folder (the server's
 * waitingReason). The server always sends its own waitingMessage with it, which wins; these are copies of the server's sentences
 * (ChatFolders.WAITING_NOT_RULED_OUT_MESSAGE / WAITING_NOT_ACCEPTED_MESSAGE, checked against __fixtures__/chatGuard.r16q.json),
 * used only if a server sent the reason alone. An unknown reason with no message draws no line (never a sentence V3 made up).
 */
export const WAITING_REASON_WORDS = {
  request_not_ruled_out: 'Waiting: this conversation was last read in a list that also shows message requests (Instagram\'s All tab), so it may be one.'
    + ' Nothing is sent until the worker reads it in Primary or General; if that does not happen before the reply expires, it ends as expired.',
  request_not_accepted: 'Waiting: this conversation is now in the message requests. Nothing is sent unless the request is accepted on the phone and the'
    + ' worker reads it in Primary or General; if that does not happen before the reply expires, it ends as expired.',
};

/** r16q: the waiting line of a reply ({text, source: 'server' | 'copy'}), or null (not queued, no reason, or no words for it). */
export function waitingLineOf(reply) {
  if (!reply || reply.status !== 'queued' || !reply.waitingReason) return null;
  if (reply.waitingMessage) return { text: reply.waitingMessage, source: 'server' };
  const copy = WAITING_REASON_WORDS[reply.waitingReason];
  return copy ? { text: copy, source: 'copy' } : null;
}

/**
 * The sentence under a FAILED reply: V3's words for its code, else the server's `message`, else 'It was not sent.' (r16p). r16q: a
 * code of R16Q_FAIL_CODES uses V3's copy only while necro.ui.r16q.outOfView is on.
 */
export function failLineText(reply, outOfViewOn = true) {
  const code = reply && reply.failCode;
  const words = code && Object.prototype.hasOwnProperty.call(FAIL_CODE_WORDS, code) && (outOfViewOn || !R16Q_FAIL_CODES.has(code)) ? FAIL_CODE_WORDS[code] : null;
  return words || (reply && reply.message) || 'It was not sent.';
}

/** Reasons the composer is closed (`replies_allowed=false`, design 6.5) -> words. */
export const BLOCKED_REASON_WORDS = {
  conversation_detached: 'This conversation is no longer in the slot\'s inbox, so replies are closed.',
  group_reply_not_supported: 'Replies to group conversations are not supported.',
  group_unknown: 'The worker has not yet proven this is a one-to-one conversation.',
  request_not_accepted: 'This is a message request. Accept it on the phone first; the worker never accepts requests.',
  channel: 'Broadcast channels cannot be replied to.',
  peer_unverified: 'The worker has not yet read who this conversation is with.',
  peer_ambiguous: 'Two conversations in this inbox have the same name, so replies are closed for both.',
  no_recent_inbound_message: 'Replies are only possible within 30 days of the other person\'s last message.',
  outbound_streak: 'You sent 3 messages in a row without an answer. Wait for them to reply.',
  thread_daily_cap: 'This conversation has reached today\'s reply limit.',
  slot_reply_mode_off: 'Replies are off for this slot ("DM Inbox (Chats)" in the slot\'s settings).',
  slot_chat_sync_off: 'Reading this slot\'s inbox is off ("DM Inbox (Chats)" in the slot\'s settings).',
  account_replies_disabled: 'Replies are switched off for this account.',
  preview_only: 'Replies on this phone\'s Instagram version are not supported yet; you can read previews.',
  platform_not_supported: 'The inbox is Instagram-only for now.',
};

[CHAT_ERROR_WORDS, FAIL_CODE_WORDS, BLOCKED_REASON_WORDS, WAITING_REASON_WORDS].forEach((t) => Object.keys(t).forEach((k) => KNOWN_WORDS.add(k)));
// r16q: the merged server's request-guard code (a 422 at queue time, a thread's repliesBlockedReason) and the waiting line's source
// words, so the [R16P-UI] / [R16Q-UI] lines name them instead of '?'
['request_not_ruled_out', 'server', 'copy'].forEach((k) => KNOWN_WORDS.add(k));

export function blockedReasonText(code, serverMessage) {
  if (serverMessage) return serverMessage;
  return (code && BLOCKED_REASON_WORDS[code]) || 'Replies are not possible in this conversation right now.';
}

// ------------------------------------------------------------------------------------------------ threads, messages, replies

const MESSAGE_KINDS = new Set(['text', 'post_share', 'reel_share', 'story_reply', 'story_mention', 'reaction', 'link', 'call', 'voice',
  'photo', 'video', 'sticker', 'gif', 'unsupported']);

/** Non-text items are placeholders: no media in r16p, and never an <img> that points at Instagram (design 7.1). */
export const KIND_PLACEHOLDER = {
  post_share: 'Shared a post',
  reel_share: 'Shared a reel',
  story_reply: 'Replied to a story',
  story_mention: 'Mentioned you in a story',
  reaction: 'Reacted to a message',
  link: 'Link',
  call: 'Call',
  voice: 'Voice message',
  photo: 'Photo',
  video: 'Video',
  sticker: 'Sticker',
  gif: 'GIF',
  unsupported: 'Item not shown',
};

export function normaliseKind(v) {
  // the server's digest kinds carry a direction suffix (story_reply_in, reaction_in): the kind is the same
  const k = lower(v) ? lower(v).replace(/_(in|out)$/, '') : null;
  return k && MESSAGE_KINDS.has(k) ? k : (k ? 'unsupported' : 'text');
}

export function normaliseReply(raw) {
  if (!raw || typeof raw !== 'object') return null;
  const id = pick(raw, 'id', 'publicId', 'public_id');
  if (id === undefined || id === null) return null;
  // review: an unknown or missing state is 'other' (inert), never 'queued' (which offered Cancel, counted as open and polled at 30 s)
  const status = replyStatus(pick(raw, 'status', 'state')) || 'other';
  const origin = lower(pick(raw, 'origin', 'source', 'createdVia', 'created_via')) || 'dashboard';
  return {
    id: String(id),
    text: typeof raw.text === 'string' ? raw.text : null,
    textLen: num(pick(raw, 'textLen', 'text_len')),
    status,
    holdReason: lower(pick(raw, 'holdReason', 'hold_reason')),
    confirmPeer: str(pick(raw, 'confirmPeerUsername', 'confirm_peer_username', 'peerUsername', 'peer_username')),
    failCode: lower(pick(raw, 'failCode', 'fail_code', 'outcomeCode', 'outcome_code')),
    message: str(raw.message),
    origin,
    byApi: origin === 'api',
    byAi: origin === 'ai_edited' || origin === 'ai',
    keyName: str(pick(raw, 'keyName', 'key_name', 'actorName')),
    createdAt: toMs(pick(raw, 'createdAt', 'created_at')),
    claimedAt: toMs(pick(raw, 'claimedAt', 'claimed_at')),
    sentAt: toMs(pick(raw, 'sentAt', 'sent_at')),
    finishedAt: toMs(pick(raw, 'finishedAt', 'finished_at')),
    expiresAt: toMs(pick(raw, 'expiresAt', 'expires_at')),
    attempts: num(raw.attempts),
    again: bool(raw.again),
    verifyOpened: bool(pick(raw, 'verifyOpened', 'verify_opened')),
    // r16pm: the server's `confirmPeer` is a FLAG (DmReply.confirm_peer): no proven peer at queue time, so the worker first opens the
    // conversation once to check who it is with (Instagram shows Seen), and a HELD confirm_peer may follow. Never a username.
    peerCheck: raw.confirmPeer === true || raw.confirm_peer === true || raw.confirm_peer === 1,
    // r16q (S4, the r16pdc README section 4 item 1): the merged server's waiting reason of a QUEUED reply held for its
    // conversation's folder (ChatUiViews.reply: waitingReason / waitingMessage, absent otherwise; the public API's snake case too)
    waitingReason: lower(pick(raw, 'waitingReason', 'waiting_reason')),
    waitingMessage: str(pick(raw, 'waitingMessage', 'waiting_message')),
  };
}

export function normaliseMessage(raw) {
  if (!raw || typeof raw !== 'object') return null;
  const id = pick(raw, 'id', 'publicId', 'public_id');
  const seq = num(raw.seq);
  if ((id === undefined || id === null) && seq === null) return null;
  const dir = lower(pick(raw, 'dir', 'direction')) === 'out' ? 'out' : 'in';
  const kind = normaliseKind(raw.kind);
  return {
    id: id !== undefined && id !== null ? String(id) : `seq-${seq}`,
    seq,
    dir,
    kind,
    text: typeof raw.text === 'string' ? raw.text : null,
    purged: bool(pick(raw, 'textRemoved', 'text_removed', 'textPurged', 'text_purged', 'purged')),
    label: str(pick(raw, 'label', 'shownLabel', 'shown_label')),
    at: toMs(pick(raw, 'at', 'approxAt', 'approx_at', 'sentAt', 'sent_at')),
    atPrecision: lower(pick(raw, 'atPrecision', 'at_precision', 'precision')) || 'unknown',
    observedAt: toMs(pick(raw, 'observedAt', 'observed_at')),
    source: lower(raw.source) || (dir === 'out' ? 'worker' : null),
    edited: bool(raw.edited),
    unsent: bool(raw.unsent),
    provisional: bool(raw.provisional),
    gapBefore: bool(pick(raw, 'gapBefore', 'gap_before')),
    replyId: str(pick(raw, 'replyId', 'reply_id')),
  };
}

/** One thread row (list and detail share it). */
export function normaliseThread(raw) {
  if (!raw || typeof raw !== 'object') return null;
  const id = pick(raw, 'id', 'publicId', 'public_id');
  if (!isOpaqueId(id === undefined || id === null ? null : String(id))) return null;
  const peerRaw = raw.peer && typeof raw.peer === 'object' ? raw.peer : {};
  const display = str(pick(peerRaw, 'display', 'displayName', 'display_name')) || str(pick(raw, 'peerDisplay', 'peer_display'));
  const username = str(pick(peerRaw, 'username')) || str(pick(raw, 'peerUsername', 'peer_username'));
  const shown = display || (username ? `@${username}` : null) || 'Unknown sender';
  const previewRaw = raw.preview && typeof raw.preview === 'object' ? raw.preview : { text: typeof raw.preview === 'string' ? raw.preview : null };
  const lastReplyRaw = pick(raw, 'lastReply', 'last_reply');
  const lastReply = lastReplyRaw && typeof lastReplyRaw === 'object'
    ? { id: str(lastReplyRaw.id), status: replyStatus(pick(lastReplyRaw, 'state', 'status')) } : null;
  const allowedRaw = pick(raw, 'repliesAllowed', 'replies_allowed');
  const slotNumber = num(pick(raw, 'slotNumber', 'slot_number'));
  const slot = str(pick(raw, 'slot', 'slotName', 'slot_name')) || (slotNumber !== null ? `slot${slotNumber}` : null);
  const folder = (lower(raw.folder) || 'primary');
  const seenAt = toMs(pick(raw, 'seenAt', 'seen_at', 'openedByWorkerAt', 'opened_by_worker_at', 'seenByWorkerAt', 'seen_by_worker_at'));
  const verifiedAt = toMs(pick(raw, 'verifiedAt', 'verified_at'));
  return {
    id: String(id),
    slot,
    slotNumber: slotNumber !== null ? slotNumber : (slot && /(\d+)$/.test(slot) ? Number(slot.match(/(\d+)$/)[1]) : null),
    platform: (str(raw.platform) || 'INSTAGRAM').toUpperCase(),
    peer: { display: shown, username, initial: (display || username || '?').replace(/^@/, '').trim().charAt(0).toUpperCase() || '?' },
    picAt: num(pick(raw, 'picAt', 'pic_at')),
    isGroup: raw.isGroup === true || raw.is_group === true || raw.is_group === 1,
    folder,
    state: (lower(raw.state) || 'active'),
    detached: lower(raw.state) === 'detached',
    keyQuality: (str(pick(raw, 'keyQuality', 'key_quality')) || '').toUpperCase() || null,
    lastActivityAt: toMs(pick(raw, 'lastActivityAt', 'last_activity_at')),
    preview: {
      text: typeof previewRaw.text === 'string' ? previewRaw.text : null,
      kind: previewRaw.kind ? normaliseKind(previewRaw.kind) : 'text',
      label: str(previewRaw.label),
      dir: lower(pick(previewRaw, 'direction', 'dir')),
    },
    // r16pm: on the r16p server `unread` is the DASHBOARD's unread (ChatUiViews: t.dashUnread()) and `unreadOnInstagram` is
    // Instagram's own flag. A server that sends neither new name keeps the old reading (`unread` = Instagram's).
    unread: has(raw, 'unreadOnInstagram') ? bool(raw.unreadOnInstagram) : bool(pick(raw, 'unread', 'unreadOnIg', 'unread_on_ig')),
    unreadCount: num(pick(raw, 'unreadCount', 'unread_count')),
    dashUnread: has(raw, 'unreadOnInstagram') ? bool(pick(raw, 'dashUnread', 'dash_unread', 'unread')) : bool(pick(raw, 'dashUnread', 'dash_unread')),
    needsReply: bool(pick(raw, 'needsReply', 'needs_reply')),
    lastReply,
    openReplies: num(pick(raw, 'openReplies', 'open_replies')),
    seenAt,
    verifiedAt,
    // r16pm: the server has no verifyOnly field. A verify-open writes verified_at and opened_by_worker_at with the same time; a later
    // normal open moves opened_by_worker_at only (ChatSync). So the LAST open was a verify-open when verifiedAt >= seenAt.
    verifyOnly: pick(raw, 'verifyOnly', 'verify_only', 'openedToVerify') !== undefined
      ? bool(pick(raw, 'verifyOnly', 'verify_only', 'openedToVerify'))
      : !!(verifiedAt && seenAt && verifiedAt >= seenAt),
    neverOpen: bool(pick(raw, 'neverOpen', 'never_open')),
    repliesAllowed: allowedRaw === undefined ? null : bool(allowedRaw),
    repliesBlocked: lower(pick(raw, 'repliesBlockedReason', 'replies_blocked_reason', 'repliesAllowedReason', 'replies_allowed_reason', 'reason')),
    repliesBlockedMessage: str(pick(raw, 'repliesBlockedMessage', 'replies_blocked_message')),
    dashReadSeq: num(pick(raw, 'readSeq', 'read_seq', 'dashReadSeq', 'dash_read_seq')),
    lastSeq: num(pick(raw, 'lastSeq', 'last_seq')),
  };
}

/** The per-slot context some answers carry (`slot`): mode FULL / PREVIEW, platform, 771, 772, the Requests pill. */
export function normaliseSlotContext(raw) {
  if (!raw || typeof raw !== 'object') return null;
  const replyMode = num(pick(raw, 'replyMode', 'reply_mode', 'dmReplyMode'));
  return {
    name: str(pick(raw, 'name', 'slot', 'slotName')),
    platform: (str(raw.platform) || 'INSTAGRAM').toUpperCase(),
    mode: (str(raw.mode) || '').toUpperCase() || null,
    syncEnabled: pick(raw, 'syncEnabled', 'sync_enabled', 'inboxEnabled', 'dmInboxEnabled') === undefined
      ? null : bool(pick(raw, 'syncEnabled', 'sync_enabled', 'inboxEnabled', 'dmInboxEnabled')),
    replyMode: replyMode !== null && replyMode >= 0 && replyMode <= 2 ? replyMode : (replyMode === null ? null : 0),
    lastSyncAt: toMs(pick(raw, 'lastSyncAt', 'last_sync_at', 'lastSyncedAt', 'last_synced_at')),
    // r16pm: the r16p server names the Requests pill flag `requestsWaiting` (ChatReads.slotState; null = never synced)
    requestsUnread: bool(pick(raw, 'requestsWaiting', 'requests_waiting', 'requestsUnread', 'requests_unread')),
    supported: raw.supported === undefined ? null : raw.supported === true,
  };
}

export function normaliseThreadList(raw) {
  const r = raw && typeof raw === 'object' ? raw : {};
  const list = Array.isArray(r) ? r : (Array.isArray(r.threads) ? r.threads : (Array.isArray(r.data) ? r.data : []));
  const threads = list.map(normaliseThread).filter(Boolean);
  return {
    threads,
    dropped: list.length - threads.length,
    nextCursor: cursorOf(pick(r, 'nextCursor', 'next_cursor')),
    slot: normaliseSlotContext(r.slot),
  };
}

export function normaliseThreadDetail(raw) {
  const r = raw && typeof raw === 'object' ? raw : {};
  const thread = normaliseThread(r.thread);
  const messages = (Array.isArray(r.messages) ? r.messages : []).map(normaliseMessage).filter(Boolean);
  // thread order is `seq`, never the parsed time (design 5.7); a message without a seq keeps its place
  messages.sort((a, b) => (a.seq === null || b.seq === null ? 0 : a.seq - b.seq));
  const replies = (Array.isArray(r.replies) ? r.replies : []).map(normaliseReply).filter(Boolean);
  replies.sort((a, b) => (a.createdAt || 0) - (b.createdAt || 0));
  const lastSeqRaw = num(pick(r, 'lastSeq', 'last_seq'));
  const lastSeq = lastSeqRaw !== null ? lastSeqRaw
    : messages.reduce((m, x) => (x.seq !== null && (m === null || x.seq > m) ? x.seq : m), null);
  return {
    thread,
    messages,
    replies,
    olderCursor: cursorOf(pick(r, 'olderCursor', 'older_cursor')),
    lastSeq,
    slot: normaliseSlotContext(r.slot),
  };
}

/** Poll interval of an open thread (design 7.1): 30 s while a reply is open (queued / claimed / sending / held), else 60 s. */
export function threadPollMs(detail) {
  const open = detail && Array.isArray(detail.replies) && detail.replies.some((x) => OPEN_STATES.has(x.status));
  return open ? 30000 : 60000;
}

/**
 * Poll interval of the conversation list (review): a refetch of an infinite query re-reads EVERY loaded page, and each counts against
 * the per-account UI chat limit (120 a minute). 60 s per loaded page keeps it at about one request a minute however far the list
 * was scrolled (1 page: 60 s; 10 pages: every 10 minutes, 10 requests).
 */
export function threadListPollMs(data) {
  const pages = data && Array.isArray(data.pages) ? data.pages.length : 1;
  return 60000 * Math.max(1, pages);
}

/** The reply a thread row's chip shows: the server's lastReply, else the newest reply of the detail. */
export function lastReplyStatus(thread) {
  return thread && thread.lastReply && thread.lastReply.status ? thread.lastReply.status : null;
}

/** The last seq of an inbound message the page rendered (the HOLD basis, design 6.5): the author saw everything up to it. */
export function basisSeqOf(messages) {
  let best = null;
  (messages || []).forEach((m) => { if (m && m.seq !== null && (best === null || m.seq > best)) best = m.seq; });
  return best;
}

// ------------------------------------------------------------------------------------------------ the composer (no newlines, R23)

/**
 * What the composer keeps of a typed or pasted value in r16p: line breaks become one space (design 6.5 / 7.1: newlines are not
 * sent until proven on the phone). Returns { text, hadBreak }. Other text is kept exactly (the server normalises and answers the
 * normalised text; the queued bubble shows that).
 */
export function composerValue(v, newlinesAllowed = false) {
  const s = typeof v === 'string' ? v : '';
  if (newlinesAllowed) return { text: s, hadBreak: false };
  const hadBreak = /[\r\n\u2028\u2029]/.test(s);
  return { text: hadBreak ? s.replace(/[ \t]*(\r\n|\r|\n|\u2028|\u2029)+[ \t]*/g, ' ') : s, hadBreak };
}

/** The server's ChatText.norm, for the length check only: trim, NFC, whitespace runs to one space. */
export function normForLength(v) {
  let s = typeof v === 'string' ? v : '';
  try { s = s.normalize('NFC'); } catch (e) { /* old engines */ }
  return s.replace(/\s+/g, ' ').trim();
}

/** Characters as the server counts them (code points of the normalised text). */
export function replyLength(v) {
  return Array.from(normForLength(v)).length;
}

// ------------------------------------------------------------------------------------------------ times

const pad2 = (n) => String(n).padStart(2, '0');
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** "14:05" in the viewer's own clock. */
export function fmtClock(ms) {
  if (!Number.isFinite(ms)) return '';
  const d = new Date(ms);
  return `${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
}

/** List time: "now", "5m", "3h", "Yesterday", "Mon 12", "12 Oct" (the viewer's clock), "" when unknown. */
export function fmtListTime(ms, now = Date.now()) {
  if (!Number.isFinite(ms)) return '';
  const diff = now - ms;
  if (diff < 60000 && diff > -60000) return 'now';
  if (diff > 0 && diff < 3600000) return `${Math.floor(diff / 60000)}m`;
  const d = new Date(ms);
  const n = new Date(now);
  const sameDay = d.toDateString() === n.toDateString();
  if (sameDay) return fmtClock(ms);
  const y = new Date(now - 86400000);
  if (d.toDateString() === y.toDateString()) return 'Yesterday';
  return `${d.getDate()} ${MONTHS[d.getMonth()]}${d.getFullYear() !== n.getFullYear() ? ` ${d.getFullYear()}` : ''}`;
}

/** "today at 14:05", "yesterday at 09:10", "12 Oct at 14:05". */
export function fmtWhen(ms, now = Date.now()) {
  if (!Number.isFinite(ms)) return null;
  const d = new Date(ms);
  const n = new Date(now);
  const y = new Date(now - 86400000);
  let day;
  if (d.toDateString() === n.toDateString()) day = 'today';
  else if (d.toDateString() === y.toDateString()) day = 'yesterday';
  else day = `${d.getDate()} ${MONTHS[d.getMonth()]}${d.getFullYear() !== n.getFullYear() ? ` ${d.getFullYear()}` : ''}`;
  return `${day} at ${fmtClock(ms)}`;
}

/**
 * The transcript with its date rows (design 7.1: date rows from the raw labels). A message carrying Instagram's own separator label
 * gets a row with that label before it; otherwise, when the day of a known-precision time changes, a row with that day. Returns
 * [{ type: 'date', key, label } | { type: 'msg', key, m }].
 */
export function transcriptRows(messages, now = Date.now()) {
  const out = [];
  let lastDay = null;
  (messages || []).forEach((m, i) => {
    if (m.gapBefore) out.push({ type: 'gap', key: `gap-${m.id}` });
    if (m.label) {
      out.push({ type: 'date', key: `d-${m.id}`, label: m.label });
      lastDay = m.at && m.atPrecision !== 'unknown' ? new Date(m.at).toDateString() : lastDay;
    } else if (m.at && (m.atPrecision === 'minute' || m.atPrecision === 'day')) {
      const day = new Date(m.at).toDateString();
      if (day !== lastDay && i > 0) out.push({ type: 'date', key: `d-${m.id}`, label: fmtDayLabel(m.at, now) });
      lastDay = day;
    }
    out.push({ type: 'msg', key: m.id, m });
  });
  return out;
}

export function fmtDayLabel(ms, now = Date.now()) {
  const d = new Date(ms);
  const n = new Date(now);
  if (d.toDateString() === n.toDateString()) return 'Today';
  if (d.toDateString() === new Date(now - 86400000).toDateString()) return 'Yesterday';
  return `${d.getDate()} ${MONTHS[d.getMonth()]}${d.getFullYear() !== n.getFullYear() ? ` ${d.getFullYear()}` : ''}`;
}

// ------------------------------------------------------------------------------------------------ filters and URL state

export const FILTERS = [
  { value: 'unread', label: 'Unread' },
  { value: 'needs_reply', label: 'Needs reply' },
  { value: 'queued', label: 'Queued' },
  { value: 'held', label: 'Waiting for you' },
  { value: 'all', label: 'All' },
];
export const DETACHED_FILTER = { value: 'detached', label: 'No longer in the inbox' };
const FILTER_VALUES = new Set([...FILTERS.map((f) => f.value), DETACHED_FILTER.value]);
export const DEFAULT_FILTER = 'all';

export function filterFrom(v) {
  return FILTER_VALUES.has(v) ? v : DEFAULT_FILTER;
}

/** ?slot= takes a slot NAME (slot801) - never a handle (design 7.1). Anything else is dropped. */
export function slotParamFrom(v) {
  return typeof v === 'string' && /^[A-Za-z0-9_-]{1,40}$/.test(v) ? v : null;
}

/** ?thread= takes an opaque conversation id only. */
export function threadParamFrom(v) {
  return isOpaqueId(v) ? v : null;
}

// ------------------------------------------------------------------------------------------------ the counts strip

/** Numbers only (design 7.1): unread, needs reply, queued, held, not confirmed. */
export function countsStrip(summary) {
  const s = summary || {};
  return [
    { key: 'unread', label: 'Unread', value: s.unreadThreads, filter: 'unread', tone: 'primary' },
    { key: 'needs_reply', label: 'Needs reply', value: s.needsReply, filter: 'needs_reply', tone: 'info' },
    { key: 'queued', label: 'Queued', value: s.queuedReplies, filter: 'queued', tone: 'info' },
    { key: 'held', label: 'Waiting for you', value: s.heldReplies, filter: 'held', tone: 'warning' },
    { key: 'unconfirmed', label: 'Not confirmed', value: s.unconfirmedReplies, filter: null, tone: 'warning' },
  ];
}

export const fmtCount = (v) => (Number.isFinite(v) ? v.toLocaleString('en-US') : '-');
